/**
 * The app's frame: which screen is showing, and the tabs along the bottom.
 *
 * Adding a new screen is two steps:
 *   1. make a file in screens/, and add a <Route> for it below
 *   2. add a <Tab> for it, if it should appear in the bottom bar
 */
import { Suspense, lazy, useEffect, useState } from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import { Today } from "./screens/Today";
import { Add } from "./screens/Add";
import { Settings } from "./screens/Settings";
import { SignIn } from "./screens/SignIn";
import { SIGNED_OUT_EVENT, api } from "./lib/api";
import type { Me } from "./lib/api";

/**
 * The Scan screen is loaded only when it is opened, rather than with the rest
 * of the app.
 *
 * It is by far the biggest part of the app, because the barcode reader has to
 * carry the rules for every barcode format there is. Loading it up front would
 * make the main screen slow to open on a phone, every time, to pay for a
 * screen that is used a few times a day.
 */
const Scan = lazy(() => import("./screens/Scan").then((module) => ({ default: module.Scan })));

function Tab({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    // `end` so the Today tab is only highlighted on "/" itself, rather than
    // on every address that happens to start with it.
    <NavLink to={to} end className="tab">
      <span>{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

/**
 * Decides whether to show the app or the front door.
 *
 * The server is the only thing that actually knows -- the cookie that proves
 * who she is cannot be read by anything in the page, deliberately -- so the
 * first thing the app does is ask it.
 */
export function App() {
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    let current = true;
    api
      .me()
      .then((answer) => {
        if (current) setMe(answer);
      })
      .catch(() => {
        // The server is unreachable rather than refusing us. Show the front
        // door: it is the one screen that works with no data behind it, and
        // the button will work as soon as the server is back.
        if (current) setMe({ signedIn: false, email: null, dev: false, configured: true });
      });
    return () => {
      current = false;
    };
  }, []);

  // A session can run out at any moment. api.ts announces it once, from
  // wherever the request was made, and the whole app drops back to the door.
  useEffect(() => {
    const signedOut = () => setMe((before) => (before ? { ...before, signedIn: false } : before));
    window.addEventListener(SIGNED_OUT_EVENT, signedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, signedOut);
  }, []);

  // The moment before the server has answered. Deliberately blank rather than
  // a spinner: the answer arrives in a few milliseconds from the same machine
  // that served the page, and a spinner that flashes up and vanishes reads as
  // a glitch.
  if (!me) return null;

  if (!me.signedIn) return <SignIn configured={me.configured} />;

  return <Tracker />;
}

function Tracker() {
  return (
    <>
      <main className="app">
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/add" element={<Add />} />
          {/* Suspense shows the fallback for the moment the scanner is
              being fetched. */}
          <Route
            path="/scan"
            element={
              <Suspense fallback={<div className="empty">Getting the camera ready…</div>}>
                <Scan />
              </Suspense>
            }
          />
          <Route path="/settings" element={<Settings />} />
          {/* Any address that isn't one of the above shows the main screen,
              rather than a blank page. */}
          <Route path="*" element={<Today />} />
        </Routes>
      </main>

      <nav className="tabbar">
        <Tab to="/" icon="🌱" label="Today" />
        <Tab to="/add" icon="🍽️" label="Add" />
        <Tab to="/scan" icon="📷" label="Scan" />
        <Tab to="/settings" icon="⚙️" label="Settings" />
      </nav>
    </>
  );
}
